import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators'
import store from '.'
import { AbstractNode } from '@/orrm/model'
import { Layer } from '@/orrm/model/layer'
import { Actualite } from '@/orrm/model/actualite'
import { InfoReglementation } from '@/orrm/model/info-reglementation'
import { PluralResponse } from 'coloquent'
import { FilterSpec } from 'coloquent/dist/FilterSpec'
import { Entity } from '@/orrm/model/entity'
import { EntityType } from '@/orrm/model/entity-type'
import { InfoBundle } from '@/orrm/model/info-bundle'
import { Departement } from '@/orrm/model/departement'
import { Phenomene } from '@/orrm/model/phenomene'
import {
  ActuCentroidAttributes,
  EntiteCentroidAttributes,
  InfoCentroidAttributes
} from '@/orrm/model/centroid'
import Feature from 'ol/Feature'

const _namespace = 'search'

export class EntityFilterCriteria {
  types: EntityType[] = []
}

export class InfosFilterCriteria {
  bundles: InfoBundle[] = []
}

export class ActuFilterCriteria {
  countries: ('ITA' | 'FRA')[] = []
  departements: Departement[] = []
  phenomene: Phenomene | null = null
  year: number | null = null
}

@Module({ dynamic: true, store, namespaced: true, name: _namespace })
export default class SearchModule extends VuexModule {
  search = ''
  loading = false
  results: AbstractNode[] = []
  selected: AbstractNode | null = null
  readonly sources = [
    Actualite,
    InfoReglementation,
    Layer,
    Entity
  ]

  actuCriterias: ActuFilterCriteria = new ActuFilterCriteria()
  entityCriterias: EntityFilterCriteria = new EntityFilterCriteria()
  infoCriterias: InfosFilterCriteria = new InfosFilterCriteria()

  get actuFilter () {
    return (actuFeatures: Feature<any>[]) => {
      return actuFeatures.filter(actuFeature => {
        const attributes = actuFeature.get('attributes') as ActuCentroidAttributes
        if (this.actuCriterias.countries.length > 0) {
          if (this.actuCriterias.countries.indexOf(attributes.pays) === -1) {
            return false
          }
        }

        if (this.actuCriterias.departements.length > 0) {
          if (!this.actuCriterias.departements.find(departement => departement.name === attributes.departement)) {
            return false
          }
        }

        if (this.actuCriterias.phenomene) {
          if (this.actuCriterias.phenomene.name !== attributes.phenomene) {
            return false
          }
        }

        if (this.actuCriterias.year) {
          const actuYear = attributes.created.substr(0, 4)
          if (actuYear !== this.actuCriterias.year.toString()) {
            return false
          }
        }
        return true
      })
    }
  }

  get infosFilter () {
    return (infoFeatures: Feature<any>[]) => {
      return infoFeatures.filter(infoFeature => {
        const attributes = infoFeature.get('attributes') as InfoCentroidAttributes
        if (this.infoCriterias.bundles.length > 0) {
          if (!this.infoCriterias.bundles.find(bundle => bundle.internalName === attributes.type)) {
            return false
          }
        }
        return true
      })
    }
  }

  get entityFilter () {
    return (entityFeatures: Feature<any>[]) => {
      return entityFeatures.filter(entityFeature => {
        const attributes = entityFeature.get('attributes') as EntiteCentroidAttributes
        if (this.entityCriterias.types.length > 0) {
          if (!this.entityCriterias.types.find(type => type.name === attributes.type)) {
            return false
          }
        }
        return true
      })
    }
  }

  @Mutation
  setSelected (selected: AbstractNode | null) {
    this.selected = selected
  }

  @Mutation
  setResults (results: AbstractNode[]) {
    this.results = results
  }

  @Mutation
  setSearch (search: string | null) {
    this.search = search === null ? '' : search
  }

  @Mutation
  setLoading (loading: boolean) {
    this.loading = loading
  }

  @Action({ rawError: true })
  select (selected: AbstractNode | null) {
    this.setSelected(selected)
  }

  @Action({ rawError: true })
  async makeSearch (search: string): Promise<AbstractNode[]> {
    this.setSearch(search)
    this.setLoading(true)

    const results = []
    try {
      for (const source of this.sources) {
        const qb = source.getQueryBuilder()
        const query = qb.getQuery()

        const queryKey = source.searchField.replaceAll(' ', '-').toLowerCase()
        query.addFilter(new FilterSpec(`${queryKey}][path`, source.searchField))
        query.addFilter(new FilterSpec(`${queryKey}][operator`, 'CONTAINS'))
        query.addFilter(new FilterSpec(`${queryKey}][value`, this.search))
        const result = await qb.get(0) as PluralResponse
        const data = result.getData() as AbstractNode[]
        if (data) {
          results.push(...data)
        }
      }
    } finally {
      this.setLoading(false)
    }
    return results
  }
}
